import qs from 'qs';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import createMessagingService from 'services/MessagingService/MessagingService';
import {
  GENERIC_ERROR,
  GET_PERFORMER_TIPPING_SESSION,
  GET_PERFORMER_TIPPING_SESSION_ERROR,
  GET_TIPPING_INFO,
  SEND_PRIVATE_TIP,
  SEND_PRIVATE_TIP_ERROR,
  SEND_PRIVATE_TIP_INSUFFICIENT_FUNDS,
  SEND_PRIVATE_TIP_RESPONSE,
  SEND_TIP,
  SEND_TIP_ERROR,
  SEND_TIP_ERROR_FUNDS,
  SEND_TIP_RESPONSE,
  SEND_TIP_RESPONSE_STATUS,
  SET_PERFORMER_GOAL_MESSAGE,
  TIPPING_INFO_ERROR,
  UPDATE_BALANCE,
} from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import TrackingService from 'services/TrackingService';
import APIStates from 'enum/APIStates';
import { getDomain } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';


const TIPPING_BASE_URL = ConfigurationService.get(ConfigurationKeys.TIPPING_BASE_URL);
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const TIPPING_POLLING_URL = `${TIPPING_BASE_URL}/tipper/tipping.user.php`;
const BROADCASTER_TIPPING_URL = `${TIPPING_BASE_URL}/tipper/tipping.class.php`;
const TIPPING_OPERATION_URL = `${BASE_URL}/tippingOperation`;
const PRIVATE_TIPPING_URL = `${BASE_URL}/rest/v1.0/profile/{username}/privateTip`;

/**
 * @class handles bussiness logic for tipping
 */
export class TippingService {
  TIPPING_TYPES = {
    BEAT_THE_LEADER: 'beatTheLeader',
    QUICK_TIP: 'quickTip',
    CUSTOM_TIP: 'customTip',
  };

  /**
   * Constructor of @TippingService class.
   * @param {function} createMessagingService a MessagingService creator
   * Bind methods to messaging service events
   */
  constructor(createMessagingService) {
    this.messagingService = createMessagingService();
    this.messagingService.on(SEND_TIP, this.sendTip);
    this.messagingService.on(SEND_PRIVATE_TIP, this.sendPrivateTip);
  }

  destructor() {
    this.messagingService.off(SEND_TIP, this.sendTip);
    this.messagingService.off(SEND_PRIVATE_TIP, this.sendPrivateTip);
    this.messagingService = null;
  }

  /**
   * @async
   * Method for tipping performer in live show
   * @param {Object} data with next properties:
   * type, sessionId, modelId, amount, stealthMode, viewer, accessHash, kiirooRoom
   * @returns promise
   * @emits SEND_TIP_RESPONSE when API call is successful
   * @emits SEND_TIP_ERROR when API call fails
   * @emits UPDATE_BALANCE when there is userbalance in response
   */
  sendTip = (data, tipType) => {
    this.messagingService.emit(SEND_TIP_RESPONSE_STATUS, APIStates.PENDING);

    return ApiService.post(TIPPING_OPERATION_URL, {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        const { viewer: username, modelId: roomName, accessHash, sessionId } = data;
        const user = { accessHash };
        this.getTippinginfo(username, roomName, user, sessionId);
        this.messagingService.emit(SEND_TIP_RESPONSE_STATUS, APIStates.LOADED);
        if (res.data.status !== '0') {
          switch (res.data.response) {
            case 'Fast tipping detected':
              return;
            case 'Insufficient funds':
              this.messagingService.emit(SEND_TIP_ERROR_FUNDS, res.data.response);
              break;
            default:
              this.messagingService.emit(GENERIC_ERROR);
          }
        } else {
          this.messagingService.emit(SEND_TIP_RESPONSE, {
            ...res.data,
            eventType: tipType,
          });
          TrackingService.track({
            event: 'SendTip',
            eventCategory: 'tipping',
            eventAction: 'OnlineTip',
            eventValue: data.amount,
            eventType: tipType,
          });
        }
      })
      .catch((err) => {
        this.messagingService.emit(SEND_TIP_ERROR, err);
      });
  };

  /**
   * @async
   * Method for tipping performer offline
   * @param {Object} data with next properties:
   * message, receiver, tipAmount, username
   * @returns promise
   * @emits SEND_PRIVATE_TIP_RESPONSE when API call is successful
   * @emits SEND_PRIVATE_TIP_ERROR when API call fails
   * @emits SEND_PRIVATE_TIP_INSUFFICIENT_FUNDS when not enough funds to make tip
   * @emits UPDATE_BALANCE when there is userbalance in response
   */
  sendPrivateTip = (data) => {
    const parsedURL = encodeURI(PRIVATE_TIPPING_URL.replace('{username}', data.receiver));
    const postData = {
      amount: data.tipAmount,
      message: data.message,
    };

    return ApiService.post(parsedURL, { data: postData })
      .then((res) => {
        if (res.data.status !== '0') {
          this.messagingService.emit(SEND_PRIVATE_TIP_INSUFFICIENT_FUNDS, res.data.response);
        } else {
          this.messagingService.emit(SEND_PRIVATE_TIP_RESPONSE, res.data);
        }
        if (res.status === StatusCodes.OK) {
          this.messagingService.emit(UPDATE_BALANCE, res.data.userbalance);
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('v2profile.tipping.tip.sent'),
          });
          if (data.successCb) {
            data.successCb();
          }
        }
      })
      .catch((err) => {
        this.messagingService.emit(SEND_PRIVATE_TIP_ERROR, err);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('v2profile.notification.error.default'),
        });
      });
  };

  /**
   * @async
   * Method for getting the tipping session of the broadcaster for the current broadcast
   * @param {Object} user the broadcaster
   * @param {Object} sessionId the tipping session of the broadcaster
   * @param {boolean} shouldSetPerfomerMessage if event for setting message should be emmited
   * @returns promise
   * @emits GET_PERFORMER_TIPPING_SESSION when API call is successful with the data
   * @emits GET_PERFORMER_TIPPING_SESSION_ERROR when API call is not successful
   */
  getPerformerSessionTippingInfo = (user, sessionId, shouldSetPerfomerMessage) => {
    const { username, accessHash } = user;
    const params = {
      type: 'GET_HOST_SESSION',
      sessionId: sessionId || '',
      modelId: username,
      accessHash,
    };

    ApiService.get(TIPPING_OPERATION_URL, {
      params,
      header: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (shouldSetPerfomerMessage) {
          this.messagingService.emit(SET_PERFORMER_GOAL_MESSAGE, res.data);
        }
        this.messagingService.emit(GET_PERFORMER_TIPPING_SESSION, res.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_PERFORMER_TIPPING_SESSION_ERROR, error);
      });
  };

  /**
   * @async
   * Method for setting message or amount goal for current broadcaster
   * @param {Object} user the broadcaster
   * @param {string} sessionId the tipping session of the broadcaster
   * @param {boolean} isMessage if event for setting message should be emmited
   * @param {string} value value thats updated(message or amount)
   * @returns promise
   */
  setPerfomerGoal = (user, sessionId, value) => {
    const { username, accessHash } = user;
    const data = {
      amount: value,
      type: 'SET_GOAL',
      modelId: username,
      accessHash,
      sessionId: sessionId || '',
    };

    const headers = { 'content-type': 'application/x-www-form-urlencoded' };
    ApiService.post(TIPPING_OPERATION_URL, { data: qs.stringify(data), headers })
      .then(() => {
        let text = undefined;
        if (value === '') {
          text = translate('v2broadcast.goal.cleared');
        } else {
          text = translate('v2broadcast.new.goal.set');
        }

        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text,
        });
        this.getPerformerSessionTippingInfo(user, sessionId);
      })
      .catch((error) => parseCatch(error));
  };

  setPerfomerMessage = (user, sessionId, value) => {
    const domain = getDomain();
    const query = {
      callback: 'ServiceTipPerformer.messageSet',
      params: {
        operation: 'setMessage',
        'session_id': sessionId || '',
        'model_id': user.username,
        key: user.accessHash,
        lang: 'en',
        domain,
        message: value,
      },
    };
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };

    ApiService.get(BROADCASTER_TIPPING_URL, {
      params: query,
      headers,
      withCredentials: false,
    })
      .then(() => {
        const text = translate('v2broadcast.new.goal.message.set');

        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text,
        });
        this.getPerformerSessionTippingInfo(user, sessionId);
      })
      .catch((error) => parseCatch(error));
  };

  getTippinginfo = (username, roomName, user, sessionId = null) => {
    const { accessHash } = user;
    const domain = getDomain();
    const params = {
      operation: 'getUserSession',
      'session_id': sessionId || '',
      'model_id': roomName,
      'user_id': username,
      key: accessHash,
      lang: 'en',
      domain,
    };

    const url = `${TIPPING_POLLING_URL}?params=${JSON.stringify(params)}`;
    ApiService.get(url, { withCredentials: false })
      .then((res) => {
        this.messagingService.emit(UPDATE_BALANCE, parseInt(res.data.userbalance, 10));
        if (res.status === StatusCodes.OK && res.data.session_id) {
          this.messagingService.emit(GET_TIPPING_INFO, res.data);
        }
      })
      .catch((error) => {
        this.messagingService.emit(TIPPING_INFO_ERROR, error);
      });
  };
}

export default new TippingService(createMessagingService);
